/* eslint-disable no-await-in-loop, no-restricted-syntax */

import { chunk, flatten } from 'lodash';
import type { FileSystemEntry } from '@frameio/shared/transfers/types';
import { withErrorBoundary } from '@frameio/web-uploader';
import { UploaderConfigs } from '../constants';
import { getUploadItemsFromEntry } from './getUploadItemsFromEntry';
import { getUploadItemsFromFiles } from './getUploadItemsFromFiles';
import { partitionFilesAndFolders } from './partitionFilesAndFolders';
import type { UploadFileItem, UploadFolderItem } from './uploadItem';
import { createFileItem } from './uploadItem';

type ProgressItem =
  | {
      items: UploadFileItem[];
      type: 'FILE';
    }
  | {
      items: UploadFolderItem[];
      type: 'FOLDER';
    };

export const ErrorTypes = {
  FOLDER_LIMIT_EXCEEDED: 'FOLDER_LIMIT_EXCEEDED',
  FILE_LIMIT_EXCEEDED: 'FILE_LIMIT_EXCEEDED',
  EMPTY_FOLDERS_NOT_SUPPORTED: 'EMPTY_FOLDERS_NOT_SUPPORTED',
  MAX_FOLDER_DEPTH_EXCEEDED: 'MAX_FOLDER_DEPTH_EXCEEDED',
} as const;

type Errors =
  | {
      type: typeof ErrorTypes.FOLDER_LIMIT_EXCEEDED;
      data: {
        folderCount: number;
      };
    }
  | {
      type: typeof ErrorTypes.FILE_LIMIT_EXCEEDED;
      data: {
        fileCount: number;
      };
    }
  | {
      type: typeof ErrorTypes.EMPTY_FOLDERS_NOT_SUPPORTED;
    }
  | {
      type: typeof ErrorTypes.MAX_FOLDER_DEPTH_EXCEEDED;
    };

type FileSystemStreamReaderOptions = {
  chunkSize?: number;
  callbacks: {
    onProcess?: () => void;
    onProgress?: (item: ProgressItem) => void;
    onSuccess?: () => void;
    onCanceled?: () => void;
    onError?: (error: Errors) => void;
  };
};

const isFileLimitExceeded = (numOfFiles: number) =>
  typeof UploaderConfigs.MAX_FILE_COUNT_PER_JOB.value === 'undefined'
    ? false
    : numOfFiles > UploaderConfigs.MAX_FILE_COUNT_PER_JOB.value;
const isFolderLimitExceeded = (numOfFolders: number) =>
  typeof UploaderConfigs.MAX_FOLDER_COUNT_PER_JOB.value === 'undefined'
    ? false
    : numOfFolders > UploaderConfigs.MAX_FOLDER_COUNT_PER_JOB.value;

type FileType = {
  entry: FileSystemFileEntry;
  index: number;
  tempParentId: string;
};

export class FileSystemStreamReader {
  private canceled = false;

  private onProcess: Array<() => void> = [];

  private callbacks: FileSystemStreamReaderOptions['callbacks'];

  /**
   * Number of files to read per iteration.
   */
  private chunkSize: number;

  constructor(options?: FileSystemStreamReaderOptions) {
    this.chunkSize = options?.chunkSize || 100;
    this.callbacks = Object.fromEntries(
      Object.entries(options?.callbacks ?? {})
        .filter(([, callback]) => callback)
        .map(([key, callback]) => [key, withErrorBoundary(callback)])
    );
  }

  private async processFileItemChunk(items: Array<FileType>) {
    const uploadItems = [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];

      try {
        const file: File = await new Promise((resolve, reject) =>
          item.entry.file(resolve, reject)
        );
        const { tempParentId, index } = item;

        // eslint-disable-next-line no-param-reassign
        uploadItems[i] = createFileItem(tempParentId, file, index);
      } catch (error) {
        this.callbacks.onError?.(error as Errors);
      }
    }

    this.callbacks.onProgress?.({ items: uploadItems, type: 'FILE' });
  }

  /**
   * Get items to be uploaded from dropped files/folders.
   *
   * @param {FileList} files - `event.target.files`.
   * @param {DataTransferItemList} dataTransferItems - `event.dataTransfer.items`.
   * @param {number} endIndex - The max index at which to upload all the files.
   */
  private async readUploadItems(
    files: File[],
    fsEntries: FileSystemEntry[] | null,
    endIndex: number
  ): Promise<{
    folderItems: UploadFolderItem[];
    fileItems: UploadFileItem[];
  } | null> {
    // If `dataTransferItems` is supported, we can upload nested folders.
    if (fsEntries) {
      // Get all content, including nested ones. This might be a very large
      // list.
      const startIndex = endIndex - fsEntries.length + 1;
      const folderTrees = await Promise.all(
        fsEntries.map((entry, index) =>
          getUploadItemsFromEntry(entry, startIndex + index)
        )
      );
      const items = flatten(folderTrees);

      // Split folders from files to process them separately
      const [folderItems, fileItems] = partitionFilesAndFolders(items);

      if (isFileLimitExceeded(fileItems.length)) {
        this.callbacks.onError?.({
          type: ErrorTypes.FILE_LIMIT_EXCEEDED,
          data: {
            fileCount: fileItems.length,
          },
        });
        return null;
      }

      if (isFolderLimitExceeded(folderItems.length)) {
        this.callbacks.onError?.({
          type: ErrorTypes.FOLDER_LIMIT_EXCEEDED,
          data: {
            folderCount: folderItems.length,
          },
        });
        return null;
      }

      this.onProcess.push(async () => {
        // If attempting to upload folders, process all folders first
        if (folderItems.length) {
          if (this.canceled) {
            this.callbacks.onCanceled?.();
            return;
          }

          this.callbacks.onProgress?.({ items: folderItems, type: 'FOLDER' });
        }

        // Break this potentially very large list into smaller chunks
        const itemChunks = chunk(fileItems, this.chunkSize);

        for (const itemChunk of itemChunks) {
          // Check for cancelation after every chunk read.
          if (this.canceled) {
            this.callbacks.onCanceled?.();
            return;
          }

          // `processChunk` doesn't need to be scheduled as macrotask _yet_
          // because it isn't doing very much, and `item.entry.file()` is an I/O
          // operation that is already a macrotask anyway.
          //
          // However, if anything more CPU-intensive is added later, we might need
          // to put this in a `setTimeout`. Check the profiler!
          await this.processFileItemChunk(itemChunk as Array<FileType>);
        }

        this.callbacks.onSuccess?.();
      });

      return {
        fileItems,
        folderItems,
      };
    }
    // Fallback to a flat list of files if the browser that doesn't support
    // `dataTransferItems`. Note that this doesn't really need to support
    // cancellations because there's no expensive `FileSystemEntry.file()`
    // calls as above.
    const startIndex = endIndex - files.length + 1;
    const items = getUploadItemsFromFiles(files, startIndex);

    const [folderItems, fileItems] = partitionFilesAndFolders(items);

    if (!fileItems.length && !folderItems.length) {
      this.callbacks.onError?.({
        type: ErrorTypes.EMPTY_FOLDERS_NOT_SUPPORTED,
      });
      return null;
    }

    if (isFileLimitExceeded(fileItems.length)) {
      this.callbacks.onError?.({
        type: ErrorTypes.FILE_LIMIT_EXCEEDED,
        data: {
          fileCount: fileItems.length,
        },
      });
      return null;
    }

    if (isFolderLimitExceeded(folderItems.length)) {
      this.callbacks.onError?.({
        type: ErrorTypes.FOLDER_LIMIT_EXCEEDED,
        data: {
          folderCount: folderItems.length,
        },
      });
      return null;
    }

    this.onProcess.push(async () => {
      if (folderItems.length) {
        this.callbacks.onProgress?.({ items: folderItems, type: 'FOLDER' });
      }

      const itemChunks = chunk(fileItems, this.chunkSize);

      itemChunks.forEach((itemChunk) => {
        this.callbacks.onProgress?.({ items: itemChunk, type: 'FILE' });
      });

      this.callbacks.onSuccess?.();
    });
    return {
      fileItems,
      folderItems,
    };
  }

  start(files: File[], fsEntries: FileSystemEntry[] | null, endIndex: number) {
    return this.readUploadItems(files, fsEntries, endIndex);
  }

  cancel() {
    this.canceled = true;
  }

  error(error: Errors) {
    this.callbacks.onError?.(error);
  }

  process() {
    this.onProcess.forEach((fn) => fn());
  }
}
